/**
 * Resolves the `container` value for an ngb-datepicker popup.
 *
 * ng-bootstrap's `container` input is `'body' | null`, which reads cryptically at
 * call sites. Express intent with `isRenderedInModal` instead and let this map it:
 *
 * - **Not in a modal** (normal page) → `'body'`: the popup is appended to `<body>`
 *   so it escapes clipping/overflow parents (cards, form fields). This is the
 *   app-wide default set in AppModule.
 * - **In a modal** (MatDialog) → `null`: the popup renders inline, inside the
 *   dialog's DOM subtree. Required because CDK v22 dialogs render in the browser
 *   **top layer**, which sits above all normal-DOM content regardless of z-index —
 *   so a `<body>`-appended popup renders *behind* the dialog and is invisible.
 */
export type DatepickerContainer = 'body' | null;

/**
 * The two ngb-datepicker `container` values, named by where the component lives:
 *   - `page`  → `'body'` (normal pages; popup escapes clipping parents)
 *   - `modal` → `null`   (inside a MatDialog; popup renders inline so it isn't
 *                          occluded by the dialog's top layer)
 */
export const DATEPICKER_CONTAINER: Record<'page' | 'modal', DatepickerContainer> = {
  page: 'body',
  modal: null,
};
